import React, { useState } from 'react';
import CountryIcon from '~/UI/CountryIcon';

import classNames from 'classnames';

import type { ErpScenarioResponse, VirtualPackErpResponse } from 'com.cogvio.pricemonitor.api';
import { PriceSummary } from './PriceSummary';
import { ChartBar } from './ChartBar';
import { ChartLine } from './ChartLine';
import { SpinningLoadingIcon } from '~/libs/ui/atoms/SpinningLoadingIcon';
import { ERPApiResult, InterventionTypeListTitles } from '~/libs/infrastructure/schemas/erp';

interface InterventionScenarioProps {
	currency: string;
	scenarioData: VirtualPackErpResponse | undefined;
	parseErpResults: ERPApiResult | undefined;
	index: number;
	scenario: ErpScenarioResponse;
	loading: boolean;
	virtualProductPackName: string;
	onScenarioEdit: (name: string) => void;
}

export function InterventionScenario({
	currency,
	scenarioData,
	parseErpResults,
	index,
	scenario,
	loading,
	virtualProductPackName,
	onScenarioEdit,
}: InterventionScenarioProps) {
	const [active, setActive] = useState(true);

	return (
		<div className='scenario-item text-left' key={scenario.id}>
			<div className={classNames('block border-b border-gray-300')}>
				{loading && (
					<div className='w-full pt-5 flex justify-center items-center'>
						<div className='h-12'>
							<SpinningLoadingIcon />
						</div>
					</div>
				)}
				{!loading && scenarioData && (
					<>
						<div className='relative pl-10 pt-4 pb-4'>
							<div className='mb-2 flex items-start justify-between min-h-9'>
								<div className='flex items-start'>
									<div className='absolute left-0 top-4 flex items-center justify-center w-8 h-8 font-display font-semibold text-label leading-8 text-gray-600 rounded-full bg-gray-100'>
										{index}#
									</div>
									<button
										type='button'
										onClick={(event) => {
											event.preventDefault();
											setActive((prev) => !prev);
										}}
									>
										<h3 className='mt-1 font-display text-lg leading-tight font-semibold text-cPurple-800 tracking-wider tabular-nums text-left transtion hover:text-cPurple-600 underline'>
											{scenario.name}
										</h3>
									</button>
									<div className='relative -top-0.5 ml-2 flex flex-wrap items-start'>
										{scenario.interventions.map((lang) => (
											<span
												className='ml-1 mb-1 inline-block px-1.5 py-1 rounded-sm border border-gray-100 font-display text-labelsmall font-medium leading-3 text-cBlue-800 tracking-wide bg-gray-100 bg-opacity-40'
												key={`${lang.country.alpha2}-${lang.month}`}
											>
												<strong className='inline-flex items-center justify-start'>
													<CountryIcon code={lang.country.alpha2} name={lang.country.name} size={17} />
													<div className='ml-1.5'>
														<span className='block mb-0.5 text-gray-400 font-normal'>{lang.country.name}</span>
														<span className='block text-[12px] leading-tight text-black font-semibold tabular-nums'>
															{InterventionTypeListTitles[lang.type]}
														</span>
													</div>
												</strong>
											</span>
										))}
									</div>
								</div>
								<div className='pl-4 pt-0.5 shrink-0 grow-0'>
									<button className='btn-light-small !my-0' onClick={() => onScenarioEdit(scenario.id)}>
										<svg
											xmlns='http://www.w3.org/2000/svg'
											viewBox='0 0 20 20'
											fill='currentColor'
											className='inline-block relative -mt-px w-3 h-3 mr-px'
										>
											<path
												fillRule='evenodd'
												d='M7.84 1.804A1 1 0 018.82 1h2.36a1 1 0 01.98.804l.331 1.652a6.993 6.993 0 011.929 1.115l1.598-.54a1 1 0 011.186.447l1.18 2.044a1 1 0 01-.205 1.251l-1.267 1.113a7.047 7.047 0 010 2.228l1.267 1.113a1 1 0 01.206 1.25l-1.18 2.045a1 1 0 01-1.187.447l-1.598-.54a6.993 6.993 0 01-1.929 1.115l-.33 1.652a1 1 0 01-.98.804H8.82a1 1 0 01-.98-.804l-.331-1.652a6.993 6.993 0 01-1.929-1.115l-1.598.54a1 1 0 01-1.186-.447l-1.18-2.044a1 1 0 01.205-1.251l1.267-1.114a7.05 7.05 0 010-2.227L1.821 7.773a1 1 0 01-.206-1.25l1.18-2.045a1 1 0 011.187-.447l1.598.54A6.993 6.993 0 017.51 3.456l.33-1.652zM10 13a3 3 0 100-6 3 3 0 000 6z'
												clipRule='evenodd'
											></path>
										</svg>
										&nbsp;Edit scenario
									</button>
								</div>
							</div>
							<div className='relative mb-1'>
								<PriceSummary
									currency={currency}
									monthData={scenarioData.monthData}
									cumulativeRevenues={scenarioData.stats.cumulativeRevenues}
								/>
							</div>
						</div>
						{active && parseErpResults && (
							<div className='pl-10 pb-10 flex justify-between'>
								<div className='relative grow w-full'>
									<ChartBar currency={currency} parseErpResults={parseErpResults} />
									<ChartLine result={parseErpResults} virtualProductPackName={virtualProductPackName} />
								</div>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
}
